import { useEffect, useState, useContext } from "react";
import { Box, Typography, Divider } from "@mui/material";
import { useGetMemberActivityFeed } from "services/feed";
import { AccountStub } from "protogen/common_pb";
import { Family } from "protogen/advisors_service_pb";
import { FeedEntry } from "protogen/feed_pb";
import { TaskChangeEventType } from "protogen/tasks_pb";
import { CurrentUserContext } from "components/context/RequireAuth";
import useIsMobile from "components/hooks/useIsMobile";
import { UserAvatar } from "components/common/CurrentUserAvatar";
import DateDisplay from "components/common/DateDisplay";
import LinkRouter from "components/navigation/LinkRouter";
import { StageChip } from "components/tasks/workflowStates";
import FlexBox from "components/common/FlexBox";
import TruncatedText from "components/common/TruncatedText";
import {
  getActivityDescription,
  getCommentPreview,
  combineEntries,
  getActivityTitle,
  getActivityLink,
  getActvityByline,
} from "./activityFeedUtils";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import useIsVisible from "components/hooks/useIsVisible";

const MAX_ENTRIES = 5;

interface ActivityFeedProps {
  family?: Family;
  hideActors?: boolean;
  maxEntries?: number;
}

const ActivityEntryItem = ({
  entry,
  accountMap,
  hideActor = false,
}: {
  entry: FeedEntry;
  accountMap: Record<string, AccountStub>;
  hideActor?: boolean;
}) => {
  const currentUser = useContext(CurrentUserContext);
  const isCurrentUser = entry.senderRef === currentUser.ref;
  const actor = accountMap[entry.senderRef];
  const actorName = actor?.firstName ? `${actor.firstName}` : "User";

  const displayName = isCurrentUser ? "You" : actorName;
  const description = getActivityDescription(entry, displayName);
  const activityTitle = getActivityTitle(entry);
  const activityLink = getActivityLink(entry);
  const commentPreview = getCommentPreview(entry, 100);
  const isMobile = useIsMobile();
  const activityByline = getActvityByline(entry);

  return (
    <Box sx={{ display: "flex", gap: "12px" }}>
      {!isMobile && !hideActor && (
        <Box
          sx={{
            flexShrink: 0,
            width: 24,
            height: 24,
          }}
        >
          {actor && <UserAvatar user={actor} size={24} />}
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
        }}
      >
        <FlexBox justifyContent="space-between">
          <FlexBox alignItems="center" gap="8px">
            {isMobile && actor && !hideActor && (
              <Box
                sx={{
                  flexShrink: 0,
                  width: 24,
                  height: 24,
                }}
              >
                <UserAvatar user={actor} size={24} />
              </Box>
            )}
            {entry.taskChangeEvent?.type ===
            TaskChangeEventType.TASK_CHANGE_EVENT_STAGE_CHANGED ? (
              <Typography
                variant="bodyHeavy"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                {displayName}{" "}
                {entry.taskChangeEvent?.newStage === "done"
                  ? "completed a task"
                  : "updated a task"}
              </Typography>
            ) : (
              <Typography variant="bodyHeavy">{description}</Typography>
            )}
          </FlexBox>
          <DateDisplay date={new Date(Number(entry.timestampSec) * 1000)} />
        </FlexBox>

        <FlexBox
          direction={isMobile ? "column-reverse" : "row"}
          gap="8px"
          alignItems={isMobile ? "start" : "center"}
          sx={isMobile ? { width: "100%" } : {}}
        >
          {entry.taskChangeEvent?.type ===
            TaskChangeEventType.TASK_CHANGE_EVENT_STAGE_CHANGED && (
            <StageChip
              stage={entry.taskChangeEvent.newStage}
              accountType="member"
              size="small"
            />
          )}

          <LinkRouter
            to={activityLink}
            sx={{
              padding: 0,
              display: "flex",
              minWidth: 0,
              ...(isMobile && { width: "100%" }),
            }}
          >
            <TruncatedText
              variant="bodyHeavy"
              color="primary.main"
              width={isMobile ? "100%" : undefined}
            >
              {activityTitle}
            </TruncatedText>
          </LinkRouter>
        </FlexBox>
        {activityByline && (
          <Typography variant="body" color="text.secondary">
            {activityByline}
          </Typography>
        )}

        {commentPreview && (
          <TruncatedText variant="bodySmall" color="text.secondary">
            {commentPreview}
          </TruncatedText>
        )}
      </Box>
    </Box>
  );
};

export const ActivityFeed = ({
  family,
  hideActors = false,
  maxEntries = MAX_ENTRIES,
}: ActivityFeedProps) => {
  const { request } = useGetMemberActivityFeed();
  const [entries, setEntries] = useState<FeedEntry[]>([]);
  const [accountMap, setAccountMap] = useState<Record<string, AccountStub>>({});
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();
  const { registerHandler, unregisterHandler } = useWebSocketContext();
  const { isVisible } = useIsVisible({});

  useEffect(() => {
    const pushHandler = () => {
      if (isVisible) {
        loadEntries();
      }
    };
    registerHandler(["email/", "task/"], pushHandler);
    return () => {
      unregisterHandler(["email/", "task/"], pushHandler);
    };
  }, [family?.ref, isVisible]);

  const loadEntries = async () => {
    setLoading(true);
    const response = await request({
      startCursor: "",
      markConversationsRead: false,
      pageSize: 20,
      direction: "desc",
      entryTypes: [],
    });
    if (response) {
      const filteredEntries = response.entries.filter((entry) => {
        const activityTitle = getActivityTitle(entry);
        return activityTitle !== "Unknown task";
      });

      const combinedEntries = combineEntries(filteredEntries);
      const finalEntries = combinedEntries.slice(0, maxEntries);
      setEntries(finalEntries);

      const accountsMap: Record<string, AccountStub> = {};
      response.accounts.forEach((account) => {
        accountsMap[account.ref] = account;
      });

      setAccountMap(accountsMap);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (family?.ref) {
      loadEntries();
    }
  }, [family?.ref]);

  return (
    <Box>
      <Box sx={{ marginBottom: "12px" }}>
        <FlexBox direction={"row"} justifyContent={"space-between"}>
          <Typography variant={isMobile ? "h3" : "h4"}>
            Recent activity
          </Typography>
          <LinkRouter to="/activity" useColorScheme>
            See all
          </LinkRouter>
        </FlexBox>
      </Box>

      <FlexBox variant={isMobile ? "contained" : "default"} direction="column">
        {loading ? (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="bodyHeavy">Loading activity...</Typography>
          </Box>
        ) : entries.length === 0 ? (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="bodyHeavy">No recent activity</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: "12px", flexDirection: "column" }}>
            {entries.map((entry, index) => (
              <Box
                key={`${entry.ref}-${index}`}
                sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
              >
                <ActivityEntryItem
                  key={`${entry.ref}-${index}`}
                  entry={entry}
                  accountMap={accountMap}
                  hideActor={hideActors}
                />
                {index !== entries.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        )}
      </FlexBox>
    </Box>
  );
};
